/**
 * FO-307-A1 - de offline-mutatie-wachtrij (IndexedDB `mutations`-store).
 */
import { newIdempotencyKey } from '@streep/utils'
import { STORE_MUTATIONS, idbGetAll, idbGet, idbPut, idbDelete } from './db'
import type { QueuedMutation, OfflineMutationType } from './types'

export interface MutationDescriptor {
  type: OfflineMutationType
  label: string
  payload: unknown
}

/** Bouwt een wachtrij-item (nog NIET opgeslagen) met een verse idempotency-key. */
export function buildMutation(desc: MutationDescriptor): QueuedMutation {
  const now = Date.now()
  return {
    id: `${now}-${Math.floor(Math.random() * 1_000_000)}`,
    idempotencyKey: newIdempotencyKey(),
    createdAt: now,
    status: 'pending',
    attempts: 0,
    type: desc.type,
    label: desc.label,
    payload: desc.payload,
  }
}

/** Signaleert de OfflineProvider dat de wachtrij is gewijzigd (badge verversen). */
export function notifyQueueChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('offline-queue-changed'))
  }
}

export async function enqueueMutation(desc: MutationDescriptor): Promise<QueuedMutation> {
  const mutation = buildMutation(desc)
  await idbPut(STORE_MUTATIONS, mutation)
  notifyQueueChanged()
  return mutation
}

export async function saveMutation(mutation: QueuedMutation): Promise<void> {
  await idbPut(STORE_MUTATIONS, mutation)
  notifyQueueChanged()
}

export async function listMutations(): Promise<QueuedMutation[]> {
  try {
    return await idbGetAll<QueuedMutation>(STORE_MUTATIONS)
  } catch {
    return []
  }
}

export async function getMutation(id: string): Promise<QueuedMutation | undefined> {
  return idbGet<QueuedMutation>(STORE_MUTATIONS, id)
}

export async function removeMutation(id: string): Promise<void> {
  await idbDelete(STORE_MUTATIONS, id)
  notifyQueueChanged()
}

/** Verwerp een conflict/fout-item bewust (monteur bevestigt: lokale kopie weg). */
export async function discardMutation(id: string): Promise<void> {
  await removeMutation(id)
}
